import { Link } from "react-router-dom";
import { FaArrowRight, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { motion } from "framer-motion";
import { services } from "../../data/services";
import { useRef, useState, useEffect } from "react";

const HeroServices = () => {
  const mainServices = services
    .filter((s) => !s.isLocationPage)
    .slice(0, 8);

  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile breakpoint
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Track active dot on scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isMobile) return;

    const handleScroll = () => {
      const cardWidth = el.firstChild?.offsetWidth || 280;
      const gap = 16;
      const idx = Math.round(el.scrollLeft / (cardWidth + gap));
      setActiveIndex(Math.min(idx, mainServices.length - 1));
    };

    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [isMobile, mainServices.length]);

  // Scroll to a specific card
  const scrollToIndex = (idx) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.firstChild?.offsetWidth || 280;
    const gap = 16;
    el.scrollTo({ left: idx * (cardWidth + gap), behavior: "smooth" });
  };

  const scrollPrev = () => {
    const newIdx = Math.max(0, activeIndex - 1);
    setActiveIndex(newIdx);
    scrollToIndex(newIdx);
  };

  const scrollNext = () => {
    const newIdx = Math.min(mainServices.length - 1, activeIndex + 1);
    setActiveIndex(newIdx);
    scrollToIndex(newIdx);
  };

  // Shared card renderer
  const renderCard = (service, index) => {
    const Icon = service.icon;
    return (
      <motion.div
        key={service.id}
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: index * 0.05 }}
        viewport={{ once: true }}
        className={`group relative rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 ${
          isMobile
            ? "min-w-[85vw] sm:min-w-[75vw] h-[380px] snap-center flex-shrink-0"
            : "h-[420px]"
        }`}
      >
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out group-hover:scale-110"
          style={{ backgroundImage: `url(${service.image})` }}
        />

        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/30 transition-opacity duration-500 group-hover:via-black/70" />

        {/* Content */}
        <div className="absolute inset-0 p-6 sm:p-8 flex flex-col justify-end z-10 text-white">
          {/* Icon */}
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center text-xl sm:text-2xl text-[#F59E0B] group-hover:bg-[#F59E0B] group-hover:text-white transition-all duration-500 mb-4 sm:mb-6">
            <Icon />
          </div>

          {/* Title */}
          <h3 className="text-xl sm:text-2xl font-bold text-white group-hover:text-[#F59E0B] transition-colors duration-300">
            {service.title}
          </h3>

          {/* Description */}
          <p className="text-gray-300 leading-6 mt-2 sm:mt-3 text-sm line-clamp-3 opacity-90 group-hover:text-white transition-colors duration-300">
            {service.shortDescription}
          </p>

          {/* Read More */}
          <div className="mt-4 sm:mt-6">
            <Link
              to={`/services/${service.slug}`}
              className="inline-flex items-center gap-2 font-semibold text-[#F59E0B] hover:text-white transition-colors duration-300"
            >
              Learn More
              <FaArrowRight className="group-hover:translate-x-2 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <section className="py-12 md:py-24 bg-slate-50">
      <div className="container-custom">

        {/* Heading */}

        <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">

          <span className="text-[#F59E0B] font-semibold uppercase tracking-wider">
            Our Services
          </span>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#082B57] mt-4">
            Comprehensive Construction &
            Property Solutions
          </h2>

          <p className="text-gray-600 mt-6 leading-8">
            We provide end-to-end construction,
            development, property consulting, design,
            and utility solutions tailored to meet
            your project requirements.
          </p>

        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {mainServices.map((service, index) => renderCard(service, index))}
        </div>

        {/* Mobile Horizontal Slider */}
        <div className="md:hidden relative">
          {/* Gradient edge fades */}
          <div className="absolute top-0 left-0 bottom-0 w-6 bg-gradient-to-r from-slate-50 to-transparent z-20 pointer-events-none" />
          <div className="absolute top-0 right-0 bottom-0 w-6 bg-gradient-to-l from-slate-50 to-transparent z-20 pointer-events-none" />

          {/* Scrollable container */}
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-6 scrollbar-hide px-1"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {mainServices.map((service, index) => renderCard(service, index))}
          </div>

          {/* Navigation: Dots + Arrows */}
          <div className="flex items-center justify-center gap-4 mt-4">
            {/* Prev Arrow */}
            <button
              onClick={scrollPrev}
              disabled={activeIndex === 0}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-300 ${
                activeIndex === 0
                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                  : "border-[#082B57] text-[#082B57] hover:bg-[#082B57] hover:text-white active:scale-95"
              }`}
              aria-label="Previous service"
            >
              <FaChevronLeft className="text-sm" />
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {mainServices.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setActiveIndex(i);
                    scrollToIndex(i);
                  }}
                  aria-label={`Go to service ${i + 1}`}
                  className={`rounded-full transition-all duration-300 ${
                    i === activeIndex
                      ? "w-8 h-3 bg-[#F59E0B]"
                      : "w-3 h-3 bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>

            {/* Next Arrow */}
            <button
              onClick={scrollNext}
              disabled={activeIndex === mainServices.length - 1}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-300 ${
                activeIndex === mainServices.length - 1
                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                  : "border-[#082B57] text-[#082B57] hover:bg-[#082B57] hover:text-white active:scale-95"
              }`}
              aria-label="Next service"
            >
              <FaChevronRight className="text-sm" />
            </button>
          </div>
        </div>

        {/* Button */}

        <div className="text-center mt-10 md:mt-14">

          <Link
            to="/services"
            className="inline-flex items-center gap-3 bg-[#082B57] hover:bg-[#061f3d] text-white px-8 py-4 rounded-xl font-semibold transition"
          >
            View All Services
            <FaArrowRight />
          </Link>

        </div>

      </div>
    </section>
  );
};

export default HeroServices;